import classNames from 'classnames';
import ReactDOMServer from 'react-dom/server';
import { ReactElement } from 'react';

import { formatKpi } from '@utils/Formatters';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { UseDoubleBarChartOptionsProps } from '@components/design/migrated/DoubleBarChart/hooks/useDoubleBarChart';
import { BarChartKpiDisplayNames, ColumnHeader, LeaderboardKpi } from '@models/Products';
import * as styles from './ProductsLeaderboardBarChart.module.scss';
import { ColumnConfig } from '@components/design/migrated/Table';

import {
  getFormattedChangeValue,
  getSeries,
  getValueAndChangeValues,
  isKpiShare,
  labelFormatter,
} from '@utils/TableUtils';
import { displayOption } from '../utilities/CommonOptions';
import { displayChangeType } from '../utilities/ChartUtils';
import { FactRevenue } from '@utils/pageConfig/Types';
import { getColumnSortFields } from '@components/top-selling-products/products-table-columns/ProductsTableColumns';

interface FormatterProps {
  kpiKey: any;
  kpiLabel: string | ReactElement | Element;
  selectedGrowthType: GrowthTypes;
  products: LeaderboardKpi[];
  factRevenue: FactRevenue;
  isHideChangeLabels?: boolean;
}

export const validateDisplayChange = (growthType: GrowthTypes, kpiKey: any) => {
  if (growthType === GrowthTypes.percentage && isKpiShare(kpiKey)) {
    return 'pp';
  } else if (growthType === GrowthTypes.percentage) {
    return '%';
  } else {
    return '';
  }
};

export const displayHeader = (row: any) => {
  return (
    <div className={styles['ProductsLeaderboardBarChart__Tooltip__RowHeading']}>
      <div>
        <div className={styles['ProductsLeaderboardBarChart__Tooltip__RowHeading__Heading']}>{row?.item.itemBrand}</div>
        <div>{row?.item.itemName}</div>
      </div>
    </div>
  );
};

export const getProductsChartOptions = ({
  kpiKey,
  kpiLabel,
  selectedGrowthType,
  products,
  factRevenue,
  isHideChangeLabels = false,
}: FormatterProps): UseDoubleBarChartOptionsProps => {
  const series = getSeries(products, kpiKey, selectedGrowthType, isKpiShare);

  const rightFormatter = (value: number): string =>
    formatKpi(kpiKey, value, {
      currencySymbol: factRevenue.currencySymbol,
      as: displayOption(kpiKey, selectedGrowthType),
    });

  const leftFormatter = (value: number): string =>
    formatKpi(kpiKey, value, { currencySymbol: factRevenue.currencySymbol });

  const rowData = products.map((item) => {
    return {
      ...item,
      label: `<div style=" font-weight: bold; max-height: 60px; min-width: 125px; white-space: normal;">
              <b>${labelFormatter(item.item.itemBrand, 30)}</b>
              ${labelFormatter(item.item.itemName, 30)}</div>`,
      rowHeight: 52,
    };
  });

  const tooltipFormatter = (row: any = {}): string => {
    const valueAndChangeValue = getValueAndChangeValues(kpiKey, selectedGrowthType, isKpiShare, row);
    const changeValue: number = valueAndChangeValue.changeValue ?? 0;
    const value: number = valueAndChangeValue.value;

    const formattedChangeValue = getFormattedChangeValue(
      kpiKey,
      changeValue,
      selectedGrowthType,
      factRevenue.currencySymbol,
    );

    const formattedKpi = formatKpi(kpiKey, value, { currencySymbol: factRevenue.currencySymbol });

    const displayTooltips = () => {
      return (
        <div>
          <div className={styles['ProductsLeaderboardBarChart__Tooltip__Row']}>
            <p className={styles['ProductsLeaderboardBarChart__Tooltip__Label']}>{kpiLabel}</p>
            <p className={styles['ProductsLeaderboardBarChart__Tooltip__Value']}>{formattedKpi}</p>
          </div>
          {!isHideChangeLabels && (
            <div className={styles['ProductsLeaderboardBarChart__Tooltip__Row']}>
              <p className={styles['ProductsLeaderboardBarChart__Tooltip__Label']}>
                {kpiLabel} Change {displayChangeType(selectedGrowthType, kpiKey)}
              </p>
              <p
                className={classNames(
                  styles['ProductsLeaderboardBarChart__Tooltip__Value'],
                  {
                    [styles['ProductsLeaderboardBarChart__Tooltip__Value--positive']]: changeValue > 0,
                  },
                  {
                    [styles['ProductsLeaderboardBarChart__Tooltip__Value--negative']]: changeValue < 0,
                  },
                )}
              >
                {formattedChangeValue}
              </p>
            </div>
          )}
        </div>
      );
    };

    return ReactDOMServer.renderToString(
      <div className={styles.ProductsLeaderboardBarChart__Tooltip} data-testid={'chart-tooltip'}>
        {displayHeader(row)}
        {displayTooltips()}
      </div>,
    );
  };

  return {
    leftTitle: 'Absolute',
    rightTitle: 'Change',
    leftFormatter,
    rightFormatter,
    tooltipFormatter,
    rows: rowData,
    series,
  };
};

export const getBarChartDropwdownKpi = (productSpecificationFilters, isAbsoluteSortOnly: boolean): ColumnConfig[] => {
  const commonColumnFields = {
    className: 'z-20',
    isSortable: true,
    sortFields: getColumnSortFields(productSpecificationFilters, isAbsoluteSortOnly),
    compact: true,
  };

  return [
    {
      headerKey: ColumnHeader.marketRevenue,
      name: BarChartKpiDisplayNames[ColumnHeader.marketRevenue],
    },
    {
      headerKey: ColumnHeader.marketExcludeRetailRevenue,
      name: BarChartKpiDisplayNames[ColumnHeader.marketExcludeRetailRevenue],
    },
    {
      headerKey: ColumnHeader.marketShareRevenue,
      name: BarChartKpiDisplayNames[ColumnHeader.marketShareRevenue],
    },
    {
      headerKey: ColumnHeader.marketVolume,
      name: BarChartKpiDisplayNames[ColumnHeader.marketVolume],
    },
    {
      headerKey: ColumnHeader.marketExcludeRetailVolume,
      name: BarChartKpiDisplayNames[ColumnHeader.marketExcludeRetailVolume],
    },
    {
      headerKey: ColumnHeader.marketShareVolume,
      name: BarChartKpiDisplayNames[ColumnHeader.marketShareVolume],
    },
    {
      headerKey: ColumnHeader.marketPrice,
      name: BarChartKpiDisplayNames[ColumnHeader.marketPrice],
    },
    {
      headerKey: ColumnHeader.marketExcludeRetailPrice,
      name: BarChartKpiDisplayNames[ColumnHeader.marketExcludeRetailPrice],
    },
    {
      headerKey: ColumnHeader.retailerRevenue,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerRevenue],
    },
    {
      headerKey: ColumnHeader.retailerShareRevenue,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerShareRevenue],
    },
    {
      headerKey: ColumnHeader.retailerMixRevenue,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerMixRevenue],
    },
    {
      headerKey: ColumnHeader.retailerVolume,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerVolume],
    },
    {
      headerKey: ColumnHeader.retailerShareVolume,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerShareVolume],
    },
    {
      headerKey: ColumnHeader.retailerMixVolume,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerMixVolume],
    },
    {
      headerKey: ColumnHeader.retailerPrice,
      name: BarChartKpiDisplayNames[ColumnHeader.retailerPrice],
    },
  ].map((column) => {
    return { ...commonColumnFields, ...column };
  });
};
